import type { ReactNode } from 'react'
import {
  EmptyState,
  EmptyStateDescription,
  EmptyStateTitle,
} from '@/components/EmptyState'
import { EditorList } from '@/features/legends/components/EditorList'
import styles from './RoutingPlans.module.css'

export interface RoutingPlansProps {
  /**
   * The plans' rows.
   */
  children?: ReactNode
  isEmpty: boolean
  /**
   * Shows the add button.
   */
  onAdd?: () => unknown
  isDisabled?: boolean
  testId?: string
}

/**
 * The routing plans, in the order they run.
 */
export function RoutingPlans({
  children,
  isEmpty,
  onAdd,
  isDisabled,
  testId,
}: RoutingPlansProps) {
  return (
    <EditorList
      title="Routing plans"
      iconName="legend-builder-routing-plans"
      onAdd={onAdd}
      addLabel="Add routing plan"
      isDisabled={isDisabled}
      testId={testId}
    >
      {isEmpty ? (
        <div className={styles.empty}>
          <EmptyState size="sm">
            <EmptyStateTitle>No routing plans configured</EmptyStateTitle>
            <EmptyStateDescription>
              <div className={styles.emptyDescription}>
                <p>
                  Routing plans are used to temporarily narrow down the pool of
                  candidates by preference.
                </p>
                <p>
                  Plans are executed in the order they appear. When a plan
                  elapses, the next plan becomes active, but anyone who matches
                  the requirements for elapsed plans will see and be able to
                  pick up the request, even if they don&apos;t match the
                  requirements of the currently active plan.
                </p>
                <p>
                  When all routing plans have elapsed, only the qualifying
                  requirements need to be satisfied in order to match.
                </p>
              </div>
            </EmptyStateDescription>
          </EmptyState>
        </div>
      ) : (
        children
      )}
    </EditorList>
  )
}
